import { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Hidden,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  IdsExpandMore as IdsExpandMoreIcon,
  IdsExpandLess as IdsExpandLessIcon,
} from "components/shared/svgIcons";
import { makeStyles } from "@mui/styles";
import parse from "html-react-parser";

import useAuth from "hooks/useAuth";
import useCatalogDetail from "hooks/useCatalogDetail";
import { colors } from "theme";

const useStyles = makeStyles((theme: Theme) => ({
  accordion: {
    [theme.breakpoints.up("lg")]: { marginBottom: theme.spacing(5) },
    [theme.breakpoints.only("md")]: { marginBottom: theme.spacing(4) },
    [theme.breakpoints.up("sm")]: { marginTop: theme.spacing(3) },
    [theme.breakpoints.down("md")]: { marginBottom: theme.spacing(3) },
    [theme.breakpoints.down("sm")]: { marginTop: theme.spacing(2) },
  },
  accordionBtn: {
    [theme.breakpoints.down("sm")]: {
      width: "40px !important",
      minWidth: 40,
      paddingLeft: 0,
      paddingRight: 0,
      "& .MuiButton-endIcon": { margin: "0 !important" },
    },
  },
  accordionSummary: {
    minHeight: "0 !important",
    padding: 16,
    height: 64,
    boxSizing: "border-box",
    "& > .MuiAccordionSummary-content": {
      margin: 0,
    },
  },
  accordionDetails: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(0, 2, 2),
    borderTop: `1px solid ${theme.palette.grey[400]}`,
  },
  detailDesc: {
    fontSize: 16,
    letterSpacing: 0.5,
    fontWeight: 400,
    lineHeight: "26px",
    "& a": {
      color: theme.palette.secondary.main,
      fontWeight: 600,
      textDecoration: "none",
      "&:hover": { color: theme.palette.secondary.dark },
    },
    "& sup": { verticalAlign: "text-bottom" },
  },
  footnoteRow: {
    padding: "8px !important",
    borderBottom: "none",
    fontSize: "12px !important",
    lineHeight: "17px !important",
    letterSpacing: "0.5px !important",
    color: colors.nero[300],
  },
  table: {
    marginTop: 16,
    "& th, & td": {
      padding: 8,
      "&:first-child": {
        paddingLeft: 16,
      },
      "&:last-child": {
        paddingRight: 16,
      },
    },
    "& th": theme.typography.h5,
    "& td": {
      fontSize: 14,
      lineHeight: "20.41px",
      letterSpacing: 0.1,
      fontWeight: 400,
    },
  },
}));

const ContentfulDetails = () => {
  const { isLoggedIn } = useAuth();
  const { catalog } = useCatalogDetail();
  const detailFromContentful = useMemo(
    () => catalog?.designSpecification,
    [catalog?.designSpecification]
  );

  const [expanded, setExpanded] = useState(!isLoggedIn);
  const specifications = useMemo(() => {
    if (Array.isArray(detailFromContentful?.specs)) {
      return detailFromContentful!.specs.filter((detail) => detail.value);
    }
    return [];
  }, [detailFromContentful]);

  const classes = useStyles();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("lg")
  );

  if (
    !detailFromContentful ||
    (!detailFromContentful.description && !specifications.length)
  ) {
    return null;
  }

  return (
    <Accordion
      square
      expanded={expanded}
      onChange={() => {}}
      className={classes.accordion}
    >
      <AccordionSummary
        className={classes.accordionSummary}
        id="detail-desc-panel-header"
        aria-controls="detail-desc-panel-content"
      >
        <Box display="flex" justifyContent="space-between" width={1}>
          <Typography variant="h4" component="h3">
            Detail Description
          </Typography>
          <Button
            variant="text"
            size="small"
            color="secondary"
            onClick={() => setExpanded((expanded) => !expanded)}
            className={classes.accordionBtn}
            endIcon={expanded ? <IdsExpandLessIcon /> : <IdsExpandMoreIcon />}
          >
            <Hidden only="xs">{expanded ? "Collapse" : "Expand"}</Hidden>
          </Button>
        </Box>
      </AccordionSummary>
      <AccordionDetails className={classes.accordionDetails}>
        {detailFromContentful.description && (
          <Typography component="div">
            <Box my={2} className={classes.detailDesc} component="p">
              {parse(detailFromContentful.description)}
            </Box>
          </Typography>
        )}
        {specifications.length > 0 && (
          <Box width={isMobile ? "100%" : "70%"}>
            <Typography variant="h3">Specifications</Typography>
            <Table size="small" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="h5" component="span">
                      Properties
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h5" component="span">
                      Specifications
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {specifications.map((spec) => (
                  <TableRow key={spec.field}>
                    <TableCell>
                      <Box display="flex">
                        <Typography variant="inherit">
                          {parse(spec.label)}
                        </Typography>
                        {spec.annotation && <sup>{spec.annotation}</sup>}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="inherit">
                        {parse(spec.value!)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              {detailFromContentful.footnotes.length > 0 && (
                <TableFooter>
                  {detailFromContentful.footnotes.map((footnote) => (
                    <TableRow key={footnote.key}>
                      <TableCell colSpan={2} className={classes.footnoteRow}>
                        <Box display="flex">
                          <sup>{footnote.annotation}</sup>
                          <Typography variant="inherit">
                            {parse(footnote.value)}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableFooter>
              )}
            </Table>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default ContentfulDetails;
